/* eslint-disable react/no-array-index-key */
import React, { Fragment } from 'react';
import { Box, Button } from '@mui/material';
import { Grid } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { useFetchGraphQL } from '../../hooks';
import Layout from '../../layout';
import GET_ABOUT_US from '../../query/aboutUs';
import './about.scss';
import YouTubePlayer from './YouTubePlayer';
import arrow from './cheveronRight.svg';
// import PageLoader from '../../components/PageLoader/PageLoader';
import { AboutShimmer } from '../../components/Shimmer/PageShimmers';
import ServiceWeOffer from './ServiceWeOffer/ServiceWeOffer';

function About() {
  const navigate = useNavigate();
  const { loading, error, data } = useFetchGraphQL(GET_ABOUT_US);

  if (loading) {
    return (
      <Layout>
        <AboutShimmer />
      </Layout>
    );
  }
  if (error) {
    console.error('GraphQL Error:', error);
    return <p>Error! {error.message}</p>;
  }

  // Validate if about page data exists
  if (!data?.aboutUs?.blocks || !Array.isArray(data.aboutUs.blocks)) {
    console.error('About page data is missing or invalid format', data);
    return <p>Error loading page content</p>;
  }

  // Validation helper functions
  const isValidSection = (section) => section && Object.keys(section).length > 0;
  const isValidArray = (arr) => Array.isArray(arr) && arr.length > 0;
  const isValidImage = (image) => image && typeof image.url === 'string';

  const handleNavigation = (url) => {
    navigate(url);
  };

  const { blocks: aboutPageBlocks } = data.aboutUs;

  // Individual section rendering functions
  const renderHeroSection = (heroSection) => {
    if (!isValidSection(heroSection)) return null;

    return (
      <>
        <div className="hero-section-wrap">
          <div className="hero-section">
            <h1 className="title">{heroSection?.title}</h1>
            {heroSection?.subTitle && <div className="subTitle">{heroSection?.subTitle}</div>}
          </div>
        </div>
        {isValidImage(heroSection?.scrollingImage) && (
          <div className="hero-image">
            <img src={heroSection?.scrollingImage?.url} alt="" />
          </div>
        )}
      </>
    );
  };

  const renderAimSection = (aimSection) => {
    if (!isValidSection(aimSection) || !isValidArray(aimSection?.cardList)) return null;

    return (
      <div
        className="aim-section-wrap"
        style={isValidImage(aimSection?.backgroundImage) ?
          { backgroundImage: `url(${aimSection?.backgroundImage?.url})` } : {}}
      >
        {aimSection?.cardList?.map((item, index) => (
          <Fragment key={item.id || index}>
            {index % 2 === 0 ? (
              <Grid container sx={{ pt: { xs: '0px', md: '100px' } }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <div className="card-wrap">
                    {item?.heading && <span className="card-header-wrap"><span className="card-header">{item?.heading}</span></span> }
                    {item?.title && <div className="card-title">{item?.title}</div>}
                    {item?.description && (
                      <div
                        className="card-description"
                        dangerouslySetInnerHTML={{ __html: item?.description }}
                      />
                    )}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 1 }} sx={{ display: { sm: 'hidden' } }} />
                <Grid size={{ xs: 12, sm: 5 }}>
                  {isValidImage(item?.cardMedia) && (
                    <img className="card-image" src={item?.cardMedia?.url} alt={item?.title || ''} />
                  )}
                </Grid>
              </Grid>
            ) : (
              <Grid
                container
                sx={{
                  pt: { xs: '0px', sm: '60px', md: '100px' },
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                }}>
                <Grid size={{ xs: 12, sm: 5 }}>
                  {isValidImage(item?.cardMedia) && (
                    <img className="card-image" src={item?.cardMedia?.url} alt={item?.title || ''} />
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 1 }} sx={{ display: { sm: 'hidden' } }} />
                <Grid size={{ xs: 12, sm: 6 }}>
                  <div className="card-wrap">
                    {item?.heading && <div className="card-header-wrap"><span className="card-header">{item?.heading}</span> </div>}
                    {item?.title && <div className="card-title">{item?.title}</div>}
                    {item?.description && (
                      <div
                        className="card-description"
                        dangerouslySetInnerHTML={{ __html: item?.description }}
                      />
                    )}
                  </div>
                </Grid>
              </Grid>
            )}
          </Fragment>
        ))}
      </div>
    );
  };

  const renderVideoSection = (videoSection) => {
    if (!isValidSection(videoSection)) return null;

    return (
      <div className="video-section-wrap">
        <YouTubePlayer videoSection={videoSection} />
      </div>
    );
  };

  const renderScheduleSection = (scheduleSection) => {
    if (!isValidSection(scheduleSection)) return null;

    return (
      <div
        className="scheduleAppointment-section-wrap"
        style={{ backgroundImage: `url(${scheduleSection?.backgroundImage?.url})` }}
      >
        <Grid
          container
          sx={{ justifyContent: 'space-between' }}
        >
          <Grid size={{ sm: 12, md: 6, lg: 8.5 }}>
            <div className="title">{scheduleSection?.title}</div>
            <div className="subTitle" dangerouslySetInnerHTML={{ __html: scheduleSection?.description }} />
          </Grid>
          <Grid
            mt={5}
            container
            size={{ sm: 12, md: 6, lg: 3.5 }}
            sx={{
              justifyContent: { sx: 'start', md: 'end' },
              gap: '24px',
            }}
          >
            <Button size="large" variant="contained" onClick={() => handleNavigation('/contact-us')}>
              {scheduleSection?.buttonsList[0]?.label}
            </Button>
            <Button size="large" variant="outlined" onClick={() => handleNavigation('/contact-us')}>
              {scheduleSection?.buttonsList[1]?.label}
            </Button>
          </Grid>
        </Grid>
      </div>
    );
  };

  const renderPartnerSection = (partnerSection) => {
    if (!isValidSection(partnerSection)) return null;

    return (
      <div className="partener-section-wrap" style={{ backgroundImage: `url(${partnerSection?.backgroundImage?.url})` }}>
        <Grid
          container
          columnSpacing={{ xs: 1, sm: 2, md: 6 }}
          rowSpacing={{ xs: '90px', sm: 6, md: 6 }}
        >
          <Grid size={{ sm: 12, md: 4 }}>
            <Grid container sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Grid size={{ sx: 12, sm: 8, md: 12 }}>
                <div className="title">{partnerSection?.title}</div>
                <div className="subTitle">{partnerSection?.subTitle}</div>
              </Grid>
              <Grid size={{ sx: 12, sm: 4, md: 12 }} sx={{ justifyContent: 'flex-end' }}>
                <div className="button-box">
                  <Button
                    size="large"
                    variant="outlined"
                    sx={{
                      borderRadius: '1px',
                      fontSize: '16px',
                      lineHeight: '24px',
                      fontFamily: 'Lato' }}
                    onClick={() => handleNavigation('/contact-us')}
                  >
                    {partnerSection?.button?.label}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ sm: 12, md: 7.5 }}>
            <Grid container columnSpacing={{ xs: 1, sm: 8 }} rowSpacing={6}>
              {partnerSection?.cardList?.map((item, idx) => (
                <Grid
                  key={item.id || idx}
                  size={{ xs: 12, sm: 6 }}
                  className="partner-card-wrap">
                  <img className="partner-card-image" src={item?.icon?.url} alt={item?.title} />
                  <div className="partner-card-title">{item?.title}</div>
                  <div className="partner-card-subtitle">{item?.subTitle}</div>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </div>
    );
  };

  const renderBenefitsSection = (benefitsSection) => {
    if (!isValidSection(benefitsSection)) return null;

    return (
      <div className="benefits-section-wrap">
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
          }}
          className="section-top">
          <Grid size={{ xs: 7, md: 6 }}>
            <h2 className="box-title">{benefitsSection?.title}</h2>
          </Grid>
          <Grid
            container
            size={{ xs: 5, md: 5 }}
            sx={{
              justifyContent: 'flex-end',
            }}>
            <Button size="large" variant="contained" onClick={() => handleNavigation('/contact-us')}>{benefitsSection?.button?.label}</Button>
          </Grid>
          <Grid
            container
            size={{ xs: 12, md: 6 }}
            >
            <p className="box-subtitle">
              {benefitsSection?.subTitle}
            </p>
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
            marginTop: '40px',
            placeContent: 'center',
          }}
          columnSpacing={3}
          rowSpacing={3}
          >
          {benefitsSection?.cardList?.slice(0, 3).map((item, idx) => (
            <Grid
              key={item.id || idx}
              size={{ xs: 12, sm: 6, md: 4 }}
              sx={{ boxShadow: '0px 4px 6.1px 0px #00000026',
                background: '#FFFFFF',
              }}
              rowSpacing={4}
              className="benefits-card-wrap">
              <div className="card-image">
                <img
                  src={item.cardMedia?.url}
                  alt={item.title}
                  style={{ transition: 'transform 0.3s ease' }}
                  className="benefits-card-img"
                />
              </div>
              <div className="card-content">
                <div className="heading-wrap"><span className="heading">{item?.heading}</span></div>
                <div className="title">{item?.title}</div>
                <div className="card-description">{item?.description}</div>
                <Box className="action-button">
                  {item?.button?.label && (
                  <Button sx={{ mt: 'auto' }} endIcon={<img alt="" src={arrow} height={'16px'} />}>
                    {item?.button?.label.slice(0, 25)}
                  </Button>)}
                </Box>
              </div>
            </Grid>
          ))}
        </Grid>
      </div>
    );
  };

  const renderClinicSection = (clinicSection) => {
    if (!isValidSection(clinicSection)) return null;

    return (
      <div className="clinic-section">
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
          }}
          className="section-top">
          <Grid size={{ xs: 7, md: 6 }}>
            <h2 className="box-title">{clinicSection?.title}</h2>
          </Grid>
          <Grid
            container
            size={{ xs: 5, md: 5 }}
            sx={{
              justifyContent: 'flex-end',
            }}>
            <Button size="large" variant="contained" onClick={() => handleNavigation('/contact-us')}>{clinicSection?.button?.label}</Button>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <p className="box-subtitle">
              {clinicSection?.subTitle}
            </p>
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
            marginTop: '40px',
            alignItems: 'center',
            placeContent: 'center',
          }}
          columnSpacing={3}
          rowSpacing={3}
          >
          {clinicSection?.cardList?.slice(0, 3).map((item, idx) => (
            <Grid
              key={item.id || idx}
              size={{ xs: 12, sm: 6, md: 4 }}
              rowSpacing={4}
              sx={{ boxShadow: '0px 4px 4px 0px #00000040',
                background: '#FFFFFF',
              }}
              className="benefits-card-wrap">
              <div className="card-image">
                <img
                  src={item.cardMedia?.url}
                  alt={item.title}
                  style={{ transition: 'transform 0.3s ease' }}
                />
              </div>
              <div className="card-content">
                <div className="heading-wrap"><span className="heading">{item?.heading}</span></div>
                <div className="title">{item?.title}</div>
                <div className="card-description">{item?.description}</div>
                <Box className="action-button" component="span">
                  {item?.button?.label && (
                  <Button sx={{ mt: 'auto' }} endIcon={<img alt="" src={arrow} height={'16px'} />}>
                    {item?.button?.label?.slice(0, 25)}
                  </Button>)}
                </Box>
              </div>
            </Grid>
          ))}
        </Grid>
      </div>
    );
  };

  const renderCeoSection = (ceoSection) => {
    if (!isValidSection(ceoSection)) return null;

    return (
      <div className="ceo-section-wrap" style={{ backgroundImage: `url(${ceoSection?.backgroundImage?.url})` }}>
        <div className="section-top">
          <h2 className="title">{ceoSection?.heading}</h2>
          <p className="subtitle">
            {ceoSection?.subTitle}
          </p>
        </div>

        <Grid
          container
          sx={{
            justifyContent: 'center',
            marginTop: '40px',
          }}
          columnSpacing={2}
          rowSpacing={1}
          >
          {ceoSection?.ceoList?.map((item, idx) => (
            <Grid key={item.id || idx} size={{ xs: 12, sm: 6, md: 4.25 }}>
              <div className="ceo-card-wrap">
                <div className="card-image">
                  <img
                    src={item.image?.url}
                    alt={item.title}
                    className="ceo-image"
                  />
                </div>
                <div className="card-content">
                  <span className="heading">{item?.name}</span>
                  <div className="card-description">{item?.about}</div>
                </div>
              </div>
            </Grid>
          ))}
        </Grid>
      </div>
    );
  };

  const renderLocationSection = (locationSection) => {
    if (!isValidSection(locationSection) ||
     !locationSection?.locationSliderList?.length) return null;

    return (
      <div className="contactus-location-wrap">
        <div className="image-container">
          {locationSection?.locationSliderList?.slice(0, 5)?.map((location, idx) => (
            <div
              key={location.id || location.location || idx}
              className="image-wrapper"
            >
              <img
                src={`${location?.backgroundImage?.url}`}
                alt={location?.location}
              />
              <div className="mask" />
              <div className="location">{location?.location}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render a section based on its type
  const renderSection = (block) => {
    // eslint-disable-next-line no-underscore-dangle
    const blockType = block.__typename;

    switch (blockType) {
      case 'ComponentAboutUsHeroSection':
        return renderHeroSection(block);
      case 'ComponentAboutUsAimSection':
        return renderAimSection(block);
      case 'ComponentAboutUsServiceAboutUs':
        return isValidSection(block) && <ServiceWeOffer serviceSection={block} />;
      case 'ComponentSharedVideoLink':
        return renderVideoSection(block);
      case 'ComponentScheduleSectionScheduleSection':
        return renderScheduleSection(block);
      case 'ComponentPartnerCareServicesPartnerCareServices':
        return renderPartnerSection(block);
      case 'ComponentAboutBenefitsAboutBenefits': {
        // Check if this is the first or second instance of this component type
        // eslint-disable-next-line no-underscore-dangle
        const benefitsCount = aboutPageBlocks
          // eslint-disable-next-line no-underscore-dangle
          .filter((b) => b.__typename === 'ComponentAboutBenefitsAboutBenefits')
          .indexOf(block);
        return benefitsCount === 0 ? renderBenefitsSection(block) : renderClinicSection(block);
      }
      case 'ComponentCeoSectionCeoSection':
        return renderCeoSection(block);
      case 'ComponentContactUsLocationSliderList':
        return renderLocationSection(block);
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="about-wrap">
        {/* Render sections in the order they appear in the GraphQL response */}
        {aboutPageBlocks.map((block, index) => (
          <Fragment key={`block-${index}`}>
            {renderSection(block)}
          </Fragment>
        ))}
      </div>
    </Layout>
  );
}

export default About;
